import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '@/api/client'
import { getCableConsumer, waitForCableAuth } from '@/api/cable'
import type { Subscription } from '@rails/actioncable'
import type { ImportStatus } from '@/api/types'

export type ImportEvent = 'decoding' | 'staging' | 'complete' | 'failed'

export interface ImportProgress {
  event: ImportEvent
  message: string
  table?: string
  importId?: string
  importStatus?: ImportStatus
  error?: string
}

const TOKEN_KEY = 'ledger_core_token'
const POLL_INTERVAL_MS = 3_000
const WS_CONNECT_TIMEOUT_MS = 5_000

const EVENT_MESSAGES: Record<string, string> = {
  decoding: 'Reading your MYOB file…',
  staging: 'Processing accounts…',
  complete: 'Import ready for review',
  failed: 'Import failed',
}

function messageForEvent(event: string, table?: string): string {
  if (event === 'staging' && table) {
    const label = table.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    return `Processing ${label}…`
  }
  return EVENT_MESSAGES[event] || 'Processing…'
}

export function useImportProgress(orgId: string | null, importId: string | null): ImportProgress | null {
  const [progress, setProgress] = useState<ImportProgress | null>(null)
  const subRef = useRef<Subscription | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const wsConnectedRef = useRef(false)
  const wsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const startPolling = useCallback(() => {
    if (pollRef.current || !orgId || !importId) return
    pollRef.current = setInterval(async () => {
      try {
        const status = await api.getImportStatus(orgId, importId)
        if (status.status === 'pending') {
          setProgress({
            event: 'complete',
            message: EVENT_MESSAGES.complete,
            importId: status.id,
            importStatus: status,
          })
          stopPolling()
        } else if (status.status === 'failed') {
          setProgress({
            event: 'failed',
            message: EVENT_MESSAGES.failed,
            importId: status.id,
            importStatus: status,
          })
          stopPolling()
        }
      } catch {
        // Ignore poll errors - will retry on next interval
      }
    }, POLL_INTERVAL_MS)
  }, [orgId, importId, stopPolling])

  useEffect(() => {
    if (!orgId || !importId) return
    let cancelled = false

    // Set initial state
    setProgress({ event: 'decoding', message: EVENT_MESSAGES.decoding, importId })

    // Wait for ApiChannel authentication before subscribing to LedgerChannel
    waitForCableAuth().then(() => {
      if (cancelled) return

      const consumer = getCableConsumer()
      subRef.current = consumer.subscriptions.create(
        { channel: 'LedgerChannel', organisation_id: orgId },
        {
          received(data: unknown) {
            const msg = data as { type?: string; event?: string; table?: string; import_id?: string; status?: string; error?: string; errors?: string[] }
            if (msg?.type !== 'myox_import') return

            const event = msg.event as ImportEvent
            const message = messageForEvent(event, msg.table)

            if (event === 'complete' && msg.import_id) {
              api.getImportStatus(orgId, msg.import_id).then(importStatus => {
                setProgress({ event, message, importId: msg.import_id, importStatus })
              }).catch(() => {
                setProgress({ event, message, importId: msg.import_id })
              })
              stopPolling()
            } else if (event === 'failed') {
              const error = msg.error || msg.errors?.join(', ') || 'Unknown error'
              setProgress({ event, message, error })
              stopPolling()
            } else {
              setProgress({ event, message, table: msg.table, importId })
            }
          },
          connected() {
            wsConnectedRef.current = true
            // Send inline auth for this channel
            const token = localStorage.getItem(TOKEN_KEY)
            if (token && subRef.current) {
              subRef.current.perform('authenticate', { token })
            }
            if (wsTimeoutRef.current) {
              clearTimeout(wsTimeoutRef.current)
              wsTimeoutRef.current = null
            }
            stopPolling()
          },
          disconnected() {
            wsConnectedRef.current = false
            startPolling()
          },
          rejected() {
            wsConnectedRef.current = false
            startPolling()
          },
        }
      )
    })

    // If WebSocket doesn't connect within timeout, start polling
    wsTimeoutRef.current = setTimeout(() => {
      if (!wsConnectedRef.current) {
        startPolling()
      }
    }, WS_CONNECT_TIMEOUT_MS)

    return () => {
      cancelled = true
      if (subRef.current) {
        subRef.current.unsubscribe()
        subRef.current = null
      }
      stopPolling()
      if (wsTimeoutRef.current) {
        clearTimeout(wsTimeoutRef.current)
        wsTimeoutRef.current = null
      }
    }
  }, [orgId, importId, startPolling, stopPolling])

  return progress
}
